import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

export default function InteriorPaintingPage() {
  useEffect(() => {
    document.title = 'Interior Painting Durbanville | Professional Interior Painters Western Cape';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional interior painting services in Durbanville, Western Cape. Expert interior painters for homes and offices. Get quotes from the best interior painting contractors in your area.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'interior painting Durbanville, interior painters Western Cape, house painting interior, residential interior painting, commercial interior painting');
    }
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/dd8d27bf80fdddd090e193e2dd023d1c.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6" style={{ fontFamily: '"Pacifico", serif' }}>
            Interior <span style={{ color: '#e9f638' }}>PAINTING</span>
          </h1>
          <p className="text-2xl md:text-3xl mb-8 font-light">
            Transform your indoor spaces with professional interior painting
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Quote
            </a>
            <a 
              href="#services" 
              className="border-2 border-white hover:bg-white hover:text-black text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Interior Painting Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Interior Painting Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expert interior painters in Durbanville specialize in transforming your indoor spaces with precision, quality, and attention to detail.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Residential Interior</h3>
              <p className="text-gray-600">
                Complete interior painting for homes including living rooms, bedrooms, kitchens, and bathrooms with premium quality paints.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Commercial Interior</h3>
              <p className="text-gray-600">
                Professional interior painting for offices, retail spaces, and commercial buildings with minimal disruption to your business.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-palette-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Color Consultation</h3>
              <p className="text-gray-600">
                Expert color matching and consultation services to help you choose the perfect colors for your interior spaces.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-brush-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Wall Preparation</h3>
              <p className="text-gray-600">
                Complete wall preparation including filling cracks, sanding, and priming to ensure a perfect finish.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-magic-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Specialty Finishes</h3>
              <p className="text-gray-600">
                Decorative painting techniques including textured walls, feature walls, and specialty paint finishes.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Quality Assurance</h3>
              <p className="text-gray-600">
                Thorough quality checks and touch-ups to ensure your interior painting project meets the highest standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Interior Painting */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Professional Interior Painting?</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Enhanced Property Value</h3>
                    <p className="text-gray-600">Professional interior painting significantly increases your property's market value and appeal to potential buyers.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Improved Indoor Air Quality</h3>
                    <p className="text-gray-600">High-quality interior paints help seal walls and improve indoor air quality while providing long-lasting protection.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Professional Finish</h3>
                    <p className="text-gray-600">Expert painters deliver smooth, even coverage with clean lines and professional results that last for years.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Time and Cost Efficient</h3>
                    <p className="text-gray-600">Professional painters complete projects faster and more efficiently, saving you time and potential costly mistakes.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Beautiful%20modern%20interior%20room%20in%20Durbanville%20home%20with%20freshly%20painted%20walls%2C%20elegant%20furniture%2C%20natural%20lighting%2C%20contemporary%20South%20African%20design%2C%20professional%20painting%20finish%2C%20warm%20inviting%20atmosphere&width=600&height=400&seq=interior-benefits&orientation=landscape"
                alt="Professional interior painting results"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Other Painting Services</h2>
            <p className="text-xl text-gray-600">
              Explore our complete range of professional painting services in Durbanville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/exterior-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" style={{ '&:hover': { backgroundColor: '#e9f638' } }}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Exterior Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Professional exterior painting services for homes and buildings.</p>
            </a>

            <a href="/roof-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" style={{ '&:hover': { backgroundColor: '#e9f638' } }}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-2-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Roof Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Specialized roof painting and waterproofing services.</p>
            </a>

            <a href="/commercial-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" style={{ '&:hover': { backgroundColor: '#e9f638' } }}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Commercial Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Large-scale commercial and industrial painting projects.</p>
            </a>

            <a href="/residential-painting" className="group bg-gray-50 p-6 rounded-lg transition-all duration-300 cursor-pointer" style={{ '&:hover': { backgroundColor: '#e9f638' } }}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Residential Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Complete residential painting solutions for your home.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Get Your Interior Painting Quote</h2>
          <p className="text-xl text-gray-300 mb-12">
            Ready to transform your interior spaces? Get a free quote from Durbanville's best interior painters.
          </p>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <TestimonialsSlider />
        </div>
      </section>

      {/* Elfsight AI Chatbot */}
      <script src="https://elfsightcdn.com/platform.js" async></script>
      <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>

      {/* ProveSource Code */}
      <script dangerouslySetInnerHTML={{
        __html: `!function(o,i){window.provesrc&&window.console&&console.error&&console.error("ProveSource is included twice in this page."),provesrc=window.provesrc={dq:[],display:function(){this.dq.push(arguments)}},o._provesrcAsyncInit=function(){provesrc.init({apiKey:"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NvdW50SWQiOiI2NmQzYzc2NjQ3MWQwZDE2NTI2N2VkZDAiLCJpYXQiOjE3MjUxNTUxNzR9.F74G52UprvsGoyXgXsaIMG-PS-c2G7yr84Ut5rrQ4cg",v:"0.0.4"})};var r=i.createElement("script");r.type="text/javascript",r.async=!0,r["ch"+"ar"+"set"]="UTF-8",r.src="https://cdn.provesrc.com/provesrc.js";var e=i.getElementsByTagName("script")[0];e.parentNode.insertBefore(r,e)}(window,document);`
      }} />

      <Footer />
    </>
  );
}
